import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import LogoutModal from './LogoutModal';

interface WalletTransaction {
  id: number;
  transaction_type: string;
  amount: number;
  description: string;
  from_user_id?: number;
  to_user_id?: number;
  from_user_name?: string;
  to_user_name?: string;
  created_at: string;
  status: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role_name: string;
  account_balance: number;
}


const AdminWallet: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    toUserId: '',
    amount: '',
    description: ''
  });
  const [users, setUsers] = useState<User[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [activeTab, setActiveTab] = useState('wallet');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetchWalletData();
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsersForTransfer();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAdminWallet();
      console.log('🔍 [FRONTEND] API Response:', response);
      console.log('🔍 [FRONTEND] Response data:', response.data);
      console.log('🔍 [FRONTEND] Balance from API:', response.data?.data?.balance);
      setWalletBalance(response.data.data.balance || 0);
      setTransactions(response.data.data.transactions || []);
      setTotalPages(response.data.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      setWalletBalance(0);
      setTransactions([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };  


  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferData.toUserId || !transferData.amount || !transferData.description) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setTransferLoading(true);
      await adminAPI.transferToUser({
        toUserId: parseInt(transferData.toUserId),
        amount: parseFloat(transferData.amount),
        description: transferData.description
      });
      
      alert('Transfer completed successfully!');
      setShowTransferModal(false);
      setTransferData({ toUserId: '', amount: '', description: '' });
      fetchWalletData(); // Refresh wallet data
    } catch (error: any) {
      console.error('Transfer error:', error);
      alert(error.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Admin sidebar items
  const adminSidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'merchants', label: 'Merchants', icon: '🏪' },
    { id: 'reports', label: 'Reports', icon: '📈' },
    { id: 'wallet', label: 'Admin Coin Wallet', icon: '💳' },
    { id: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'credit': return '💰';
      case 'debit': return '💸';
      case 'transfer_sent': return '📤';
      case 'transfer_received': return '📥';
      default: return '💳';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'credit':
      case 'transfer_received':
        return '#10b981';
      case 'debit':
      case 'transfer_sent':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="admin-wallet">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading wallet data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wallet-page">
      {/* Desktop Sidebar */}
      <div className="admin-desktop-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <span className="admin-logo-icon">👑</span>
            <span className="admin-logo-text">Admin Panel</span>
          </div>
          <div className="admin-badge">SUPER ADMIN</div>
        </div>
        
        <nav className="admin-sidebar-nav">
          {adminSidebarItems.map(item => (
            <button
              key={item.id}
              className={`admin-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => {
                if (item.id === 'dashboard') navigate('/admin');
                else if (item.id === 'users') navigate('/admin/users');
                else if (item.id === 'merchants') navigate('/admin/merchants');
                else if (item.id === 'payouts') navigate('/admin/payouts');
                else if (item.id === 'reports') navigate('/admin/reports');
                else if (item.id === 'wallet') navigate('/admin/account_wallet');
                else if (item.id === 'maintenance') navigate('/admin/maintenance');
                else if (item.id === 'settings') navigate('/admin/settings');
              }}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileNavOpen && (
        <div className="admin-mobile-overlay" onClick={() => setMobileNavOpen(false)}></div>
      )}

      {/* Mobile Sidebar */}
      <div className={`admin-mobile-sidebar ${mobileNavOpen ? 'open' : ''}`}>
        <div className="admin-mobile-sidebar-header">
          <div className="admin-mobile-logo">
            <span className="admin-mobile-logo-icon">👑</span>
            <span className="admin-mobile-logo-text">Admin Panel</span>
          </div>
          <button 
            className="admin-mobile-close-btn"
            onClick={() => setMobileNavOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="admin-mobile-badge">SUPER ADMIN</div>
        <nav className="admin-mobile-sidebar-nav">
          {adminSidebarItems.map(item => (
            <button
              key={item.id}
              className={`admin-mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => {
                if (item.id === 'dashboard') navigate('/admin');
                else if (item.id === 'users') navigate('/admin/users');
                else if (item.id === 'merchants') navigate('/admin/merchants');
                else if (item.id === 'payouts') navigate('/admin/payouts');
                else if (item.id === 'reports') navigate('/admin/reports');
                else if (item.id === 'wallet') navigate('/admin/account_wallet');
                else if (item.id === 'maintenance') navigate('/admin/maintenance');
                else if (item.id === 'settings') navigate('/admin/settings');
                setMobileNavOpen(false);
              }}
            >
              <span className="admin-mobile-nav-icon">{item.icon}</span>
              <span className="admin-mobile-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main-content">
        {/* Admin Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileNavOpen(true)}
            >
              ☰
            </button>
            <h1>Admin Coin Wallet</h1>
            <p>Welcome back, {user?.name} - System Administrator</p>
          </div>
          <div className="admin-header-right">
            <button className="admin-btn-danger" onClick={() => navigate('/admin')}>
              <span className="btn-icon">🏠</span>
              Back to Dashboard
            </button>
            <button className="admin-btn-secondary" onClick={handleLogoutClick}>
              <span className="btn-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>

        <div className="admin-wallet">
      {/* Header */}
      <div className="wallet-header">
        <div className="header-content">
          <h1>💰 Admin Coin Wallet</h1>
          <p>Manage your account balance and transactions</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowTransferModal(true)}
          >
            <span className="btn-icon">📤</span>
            Send Money
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/admin')}
          >
            <span className="btn-icon">←</span>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Balance Card */}
      <div className="balance-card">
        <div className="balance-info">
          <h2>Coin Wallet Balance</h2>
          <div className="balance-amount">{formatCurrency(walletBalance)}</div>
          <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
            Debug: walletBalance = {walletBalance}
          </div>
          <p className="balance-subtitle">Available for transfers</p>
        </div>
        <div className="balance-icon">💳</div>
      </div>

      {/* Search */}
      <div className="search-container">
        <input
          type="text" 
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="search-input"
        />
      </div>

      {/* Transactions Table */}
      <div className="transactions-container">
        <div className="table-header">
          <h3>Coin Transaction History</h3>
          <div className="table-actions">
            <span className="transaction-count">
              {transactions?.length || 0} transactions
            </span>
          </div>
        </div>

        {!transactions || transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>No Coin transactions found</h3>
            <p>Your Coin transaction history will appear here</p>
          </div>
        ) : (
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                    </td>
                    <td>
                      {transaction.description}
                    </td>
                    <td>
                      {transaction.transaction_type.includes('debit') || transaction.transaction_type.includes('sent') ? '-' : '+'}
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td>
                      {transaction.from_user_name || '-'}
                    </td>
                    <td>
                      {transaction.to_user_name || '-'}
                    </td>
                    <td>
                      {formatDate(transaction.created_at)}
                    </td>
                    <td>
                      {transaction.status.toUpperCase()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Money</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowTransferModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body1">
              <form onSubmit={handleTransfer}>
                <div className="form-group">
                  <label>Send to:</label>
                  <select
                    value={transferData.toUserId}
                    onChange={(e) => setTransferData({...transferData, toUserId: e.target.value})}
                    className="form-control"
                    required
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}) - {user.role_name} - Balance: ₹{user.account_balance}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Amount (₹):</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                    className="form-control"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    value={transferData.description}
                    onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                    className="form-control"
                    placeholder="Enter transfer description"
                    rows={3}
                    required
                  />
                </div>

                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => setShowTransferModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={transferLoading}
                  >
                    {transferLoading ? 'Sending...' : 'Send Money'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole="Admin"
      />
    </div>
  );
};

export default AdminWallet;
